<template>
  <v-card class="chart-box" flat>
    <header class="chart-header">
      <div class="chart-eyebrow">Ulkopelikuvio</div>
      <h3 class="chart-title">{{ title }}</h3>
      <div class="chart-subtitle">{{ subtitleLabel }}</div>
    </header>

    <div class="chart-body">
      <div v-if="!canFetch" class="chart-empty">
        Valitse joukkue ja kausi.
      </div>

      <div v-else-if="loading" class="chart-loading">
        <div class="skeleton-pie" />
      </div>

      <v-alert v-else-if="error" type="error" dense outlined class="chart-error">
        {{ error }}
      </v-alert>

      <div v-else-if="!totalEvents" class="chart-empty">
        Ei dataa valituilla suodattimilla.
      </div>

      <div v-else ref="canvasWrap" class="chart-canvas-wrap">
        <canvas ref="canvas" width="400" height="260"></canvas>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import Chart from 'chart.js/auto'
import ax from '@/utils/axios/axios'
import { formationLabel, formationColor } from '@/utils/joukkueet/outfieldFormations'
import { buildFilterUrl } from '@/utils/joukkueet/filterUrl'

export default {
  name: 'OutfieldFormationPieBox',
  props: {
    leagueId: {
      type: Number,
      default: null
    },
    leagueClubId: {
      type: Number,
      default: null
    },
    seasonId: {
      type: Number,
      default: null
    },
    basesState: {
      type: String,
      default: null
    },
    hitterSide: {
      type: String,
      default: null
    },
    title: {
      type: String,
      required: true
    }
  },
  data: () => ({
    loading: false,
    error: null,
    response: null,
    requestId: 0,
    chart: null
  }),
  computed: {
    ...mapGetters('user', [
      'currentTeamId'
    ]),
    canFetch() {
      return Boolean(this.leagueId && this.leagueClubId && this.seasonId)
    },
    requestKey() {
      return [
        this.leagueId,
        this.leagueClubId,
        this.seasonId,
        this.basesState,
        this.hitterSide
      ].join(':')
    },
    totalEvents() {
      return this.response?.total_events || 0
    },
    subtitleLabel() {
      if(!this.response || !this.totalEvents) return ''
      const games = this.response.games_count || 0
      return `${this.totalEvents} tilannetta · ${games} ottelua`
    }
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.refresh()
      }
    }
  },
  beforeDestroy() {
    this.destroyChart()
  },
  methods: {
    refresh() {
      if(!this.canFetch) {
        this.response = null
        this.error = null
        this.loading = false
        this.destroyChart()
        return
      }

      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.error = null

      ax.get(`/league/${this.leagueId}/outfield-formations`, {
        params: {
          season_id: this.seasonId,
          league_club_id: this.leagueClubId,
          bases_state: this.basesState || undefined,
          hitter_side: this.hitterSide || undefined
        }
      })
        .then(res => {
          if(requestId !== this.requestId) return
          this.response = res.data
          this.$nextTick(() => this.renderChart())
        })
        .catch(err => {
          if(requestId !== this.requestId) return
          this.response = null
          this.error = err.response?.data?.error || 'Tietojen haku epäonnistui'
        })
        .finally(() => {
          if(requestId === this.requestId) this.loading = false
        })
    },
    destroyChart() {
      if(this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },
    renderChart() {
      this.destroyChart()
      if(!this.totalEvents) return

      requestAnimationFrame(() => this.createChart())
    },
    createChart() {
      const canvas = this.$refs.canvas
      if(!canvas || !this.response) return

      const formations = this.response.formations || []
      const labels = formations.map(item => formationLabel(item.formation))
      const data = formations.map(item => item.count)
      const colors = formations.map(item => formationColor(item.formation))

      const wrap = this.$refs.canvasWrap
      console.log('[OutfieldFormationPieBox]', this.title, {
        dataLen: data.length,
        sample: { labels: labels.slice(0, 3), data: data.slice(0, 3) },
        wrapSize: wrap ? { w: wrap.clientWidth, h: wrap.clientHeight } : null,
        canvasAttrs: { w: canvas.width, h: canvas.height },
        canvasClient: { w: canvas.clientWidth, h: canvas.clientHeight }
      })

      try {
        this.chart = new Chart(canvas, {
          type: 'pie',
          data: {
            labels,
            datasets: [
              {
                data,
                backgroundColor: colors,
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverOffset: 6
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            onHover: (evt, elements) => {
              if(evt.native?.target) {
                evt.native.target.style.cursor = elements.length ? 'pointer' : 'default'
              }
            },
            onClick: (_evt, elements) => {
              if(!elements.length) return
              const index = elements[0].index
              const formation = formations[index]?.formation
              if(formation) this.openFilter(formation)
            },
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  color: '#374151',
                  font: {
                    size: 12,
                    family: 'inherit'
                  },
                  boxWidth: 12,
                  boxHeight: 12,
                  padding: 12
                }
              },
              tooltip: {
                backgroundColor: '#111827',
                titleColor: '#f9fafb',
                bodyColor: '#f9fafb',
                padding: 10,
                cornerRadius: 6,
                callbacks: {
                  label: ctx => {
                    const value = ctx.parsed
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
                    const share = total ? Math.round((value / total) * 100) : 0
                    return ` ${ctx.label}: ${value} (${share}%)`
                  }
                }
              }
            }
          }
        })

        this.$nextTick(() => {
          if(this.chart) this.chart.resize()
        })
      } catch(err) {
        console.error('[OutfieldFormationPieBox] chart create failed', err)
      }
    },
    openFilter(formation) {
      if(!this.canFetch || !this.currentTeamId) return

      const overrides = {
        season: this.seasonId,
        upClubs: [this.leagueClubId],
        outfield_formation: [formation],
        league_id: this.leagueId,
        team_id: this.currentTeamId
      }

      if(this.basesState) overrides.bases_state = [this.basesState]
      if(this.hitterSide) overrides.hitter_side = [this.hitterSide]

      const url = buildFilterUrl(overrides)
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
.chart-box {
  margin-top: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chart-header {
  padding: 20px 24px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.chart-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
}

.chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.chart-subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
  min-height: 16px;
}

.chart-body {
  padding: 16px 20px 20px;
  flex: 1;
  min-height: 280px;
}

.chart-empty {
  color: #6b7280;
  font-size: 13px;
  text-align: center;
  padding-top: 40px;
}

.chart-error {
  width: 100%;
}

.chart-canvas-wrap {
  position: relative;
  width: 100%;
  height: 260px;
}

.chart-canvas-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 260px;
}

.skeleton-pie {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: conic-gradient(#f3f4f6 0%, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%, #f3f4f6 100%);
  animation: skeleton-rotate 2s linear infinite;
}

@keyframes skeleton-rotate {
  to { transform: rotate(360deg); }
}
</style>
